import type { FC } from 'react';
import * as React from 'react';
import { Card, Stack, styled, useTheme } from '@mui/material';
import IconWallet from '~icons/ant-design/wallet-filled';
import IconBankFilled from '~icons/ant-design/bank-filled';
import IconEuroCircle from '~icons/majesticons/euro-circle';
import IconFileFill from '~icons/eva/file-fill';
import IconCreditCardFilled from '~icons/ant-design/credit-card-filled';
import { TotalAnalytic } from './TotalAnalytic';
import { useTranslation } from 'react-i18next';
import type { CashList } from '@pflegenavi/frontend/api-nursing-home';
import { useCashListConfiguration } from '@pflegenavi/frontend/api-nursing-home';
import { CashListSegment, CashListStorageType } from '@pflegenavi/shared/api';

interface NewAccountingOverviewTotalsMangopayProps {
  walletInCent: number;
  receiptsInCent: number;
  cashLists: CashList[];
  payoutSumInCent: number;
  payinSumInCent: number;
}

export const NewAccountingOverviewTotalsMangopay: FC<
  NewAccountingOverviewTotalsMangopayProps
> = (props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const cashListConfiguration = useCashListConfiguration();

  const {
    cashLists,
    walletInCent,
    receiptsInCent,
    payoutSumInCent,
    payinSumInCent,
  } = props;

  const bankAccountCashLists = cashLists
    .filter(
      (cashList) =>
        cashList && cashList.storageType === CashListStorageType.BankAccount
    )
    .map((cashList) => {
      let sumInCent = cashList.totalInCent;

      if (cashListConfiguration.payoutTargetCashListId === cashList.id) {
        sumInCent += payoutSumInCent;
      }

      if (cashListConfiguration.payinTargetCashListId === cashList.id) {
        sumInCent -= payinSumInCent;
      }

      return {
        ...cashList,
        totalInCent: sumInCent,
      };
    });

  const bankAccountAmountInCent = bankAccountCashLists.reduce(
    (acc, cashList) => acc + cashList.totalInCent,
    0
  );

  const cashAccountCashLists = cashLists.filter(
    (cashList) => cashList.storageType & CashListStorageType.Cash
  );

  const cashAccountAmountInCent = cashAccountCashLists.reduce(
    (acc, cashList) => acc + cashList.totalInCent,
    0
  );

  const totalInCent =
    walletInCent +
    receiptsInCent +
    bankAccountAmountInCent +
    cashAccountAmountInCent;

  const percentages: {
    [key: string]: number;
  } = {
    wallet: (walletInCent / totalInCent) * 100,
    receipts: (receiptsInCent / totalInCent) * 100,
  };

  for (const bankCashList of bankAccountCashLists) {
    percentages[bankCashList.name] =
      (bankCashList.totalInCent / totalInCent) * 100;
  }

  for (const cashCashList of cashAccountCashLists) {
    percentages[cashCashList.name] =
      (cashCashList.totalInCent / totalInCent) * 100;
  }

  const totalItems =
    bankAccountCashLists.length + cashAccountCashLists.length + 2;

  return (
    <Stack
      direction="row"
      sx={{
        width: '100%',
        flexWrap: 'wrap',
        gap: totalItems > 4 ? 3 : 1.5,
        rowGap: totalItems > 4 ? 3 : 1.5,
      }}
    >
      <CardContainer totalItems={totalItems}>
        <TotalAnalytic
          sx={{ width: 'fit-content', minWidth: 190, padding: { xs: 0 } }}
          justifyContent="flex-start"
          title="Wallet"
          percent={percentages.wallet}
          amountInCent={walletInCent}
          Icon={IconWallet}
          color={theme.palette.info.main}
        />
      </CardContainer>

      {bankAccountCashLists.map((cashList) => (
        <CardContainer key={cashList.id} totalItems={totalItems}>
          <TotalAnalytic
            sx={{ width: 'fit-content', minWidth: 190, padding: { xs: 0 } }}
            justifyContent="flex-start"
            title={cashList.name}
            percent={percentages[cashList.name]}
            amountInCent={cashList.totalInCent}
            Icon={
              cashList.segment === CashListSegment.NursingHome
                ? IconBankFilled
                : IconCreditCardFilled
            }
            color={theme.palette.warning.main}
          />
        </CardContainer>
      ))}
      {cashAccountCashLists.map((cashList) => (
        <CardContainer key={cashList.id} totalItems={totalItems}>
          <TotalAnalytic
            sx={{ width: 'fit-content', minWidth: 190, padding: { xs: 0 } }}
            justifyContent="flex-start"
            title={cashList.name}
            percent={percentages[cashList.name]}
            amountInCent={cashList.totalInCent}
            Icon={IconEuroCircle}
            color={theme.palette.success.main}
          />
        </CardContainer>
      ))}

      <CardContainer totalItems={totalItems}>
        <TotalAnalytic
          sx={{ width: 'fit-content', minWidth: 190, padding: { xs: 0 } }}
          justifyContent="flex-start"
          title={t('receipts.main')}
          percent={percentages.receipts}
          amountInCent={receiptsInCent}
          Icon={IconFileFill}
          color={theme.palette.text.secondary}
        />
      </CardContainer>
    </Stack>
  );
};

const CardContainer = styled(Card)<{ totalItems: number }>(
  ({ totalItems }) => ({
    padding: totalItems > 4 ? 24 : 16,
    display: 'flex',
  })
);
